/**
 * ReSTIR GI。G-buffer から余弦半球レイを 1 本 SDF で飛ばし、
 * GI の SDF オフならトレースせずミス（空）扱い。
 * 前フレームの HDR 合成（画面内）を二次放射として拾う。時間フィードバックで多バウンス相当。
 * ヒットしても前フレームの画面に載せられないときは、そのサンプルは捨てる。
 * 履歴もなく有効サンプルもない画素だけ、真っ黒にしないための薄い仮サンプルを入れる。
 * リザーバの時間・空間再利用をしたあと、点光源と同じデノイザに載せる。
 */

import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import {
  GI_DIFFUSE_GAIN,
  GI_DIFFUSE_GAIN_MAX,
  GI_DIFFUSE_GAIN_MIN,
  GI_DISTANCE_FADE_END,
  GI_FIREFLY_LUMINANCE,
  GI_MAX_HISTORY_M,
  GI_SKY_WHITE_MIX,
  clampGiSlider,
} from '../shared/restir-gi-sampling';
import {
  SKY_GROUND,
  SKY_HORIZON,
  SKY_ZENITH,
} from './environment';
import type { GBuffer } from './gbuffer';
import type { GraphicsQuality } from './graphics-quality';
import { timePass, type PassTimer } from './gpu-pass-timer';
import type { AnalyticSdfGpu } from './analytic-sdf';
import type { VoxelSdfGpu } from './voxel-sdf';
import { sdfTraceGlsl } from './sdf-trace';

function fullscreenTriangle(): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute(
    'position',
    new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3)
  );
  return geometry;
}

const VERT = /* glsl */ `
out vec2 vUv;
void main() {
	vUv = position.xy * 0.5 + 0.5;
	gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const COMMON = /* glsl */ `
const float PI = 3.14159265359;
const float FIREFLY = ${GI_FIREFLY_LUMINANCE.toFixed(1)};
const float MAX_M = ${GI_MAX_HISTORY_M.toFixed(1)};
const float FADE_END = ${GI_DISTANCE_FADE_END.toFixed(1)};
const float DISOCCLUSION_LO = 0.2;

uniform float diffuseGain;
uniform sampler2D depthMap;
uniform sampler2D albedoMetalMap;
uniform sampler2D normalRoughMap;
uniform sampler3D sdfMap;
uniform sampler2D planeMap;
uniform sampler2D triangleMap;
uniform sampler2D braceMap;
uniform sampler2D latticeOriginMap;
uniform float planeCount;
uniform float triangleCount;
uniform float braceCount;
uniform float sdfMaxSteps;
uniform float sdfShadows;
uniform vec3 sdfOrigin;
uniform float sdfExtent;
uniform float sdfVoxelSize;
uniform mat4 projInverse;
uniform mat4 viewInverse;
uniform vec3 cameraPos;
uniform vec3 skyZenith;
uniform vec3 skyHorizon;
uniform vec3 skyGround;

float lightLuminance(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

uint hash(uint x) {
	x += x << 10u;
	x ^= x >> 6u;
	x += x << 3u;
	x ^= x >> 11u;
	x += x << 15u;
	return x;
}

float rand(inout uint rng) {
	rng = hash(rng);
	return float(rng) * (1.0 / 4294967295.0);
}

float sceneDepth(vec2 uv) {
	return texture(depthMap, uv).r;
}

vec3 worldFromDepth(vec2 uv) {
	float depth = sceneDepth(uv);
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = projInverse * ndc;
	view /= view.w;
	return (viewInverse * view).xyz;
}

struct Surface {
	vec3 albedo;
	vec3 normal;
	vec3 geoNormal;
	float roughness;
	float metalness;
};

vec3 worldDelta(vec3 toward, vec3 away, vec3 p) {
	vec3 a = toward - p;
	vec3 b = p - away;
	return dot(a, a) < dot(b, b) ? a : b;
}

vec3 geoNormalFromDepth(vec2 uv, vec3 p) {
	ivec2 size = textureSize(depthMap, 0);
	vec2 texel = 1.0 / vec2(size);
	vec3 dpdx = worldDelta(worldFromDepth(uv + vec2(texel.x, 0.0)), worldFromDepth(uv - vec2(texel.x, 0.0)), p);
	vec3 dpdy = worldDelta(worldFromDepth(uv + vec2(0.0, texel.y)), worldFromDepth(uv - vec2(0.0, texel.y)), p);
	vec3 n = cross(dpdx, dpdy);
	if (dot(n, n) < 1e-12) n = vec3(0.0, 1.0, 0.0);
	else n = normalize(n);
	vec3 V = normalize(cameraPos - p);
	if (dot(n, V) < 0.0) n = -n;
	return n;
}

Surface surfaceFromGBuffer(vec2 uv, vec3 p) {
	vec4 albedoMetal = texture(albedoMetalMap, uv);
	vec4 normalRough = texture(normalRoughMap, uv);
	Surface s;
	s.albedo = albedoMetal.rgb;
	s.metalness = albedoMetal.a;
	s.roughness = max(normalRough.a, 0.045);
	s.geoNormal = geoNormalFromDepth(uv, p);
	vec3 packed = normalRough.xyz * 2.0 - 1.0;
	float packedLen = length(packed);
	if (packedLen > 0.4) s.normal = packed / packedLen;
	else s.normal = s.geoNormal;
	return s;
}

vec3 skyIrradianceColor(vec3 dir) {
	vec3 d = normalize(dir);
	float h = d.y;
	vec3 sky = mix(skyHorizon, skyZenith, clamp(h * 0.85 + 0.15, 0.0, 1.0));
	vec3 below = mix(skyHorizon, skyGround, clamp(-h * 1.4, 0.0, 1.0));
	return mix(mix(below, sky, step(0.0, h)), vec3(1.0), ${GI_SKY_WHITE_MIX.toFixed(2)});
}

vec3 clampFirefly(vec3 c) {
	float lum = lightLuminance(c);
	if (lum <= FIREFLY || lum < 1e-8) return c;
	return c * (FIREFLY / lum);
}

vec2 octEncode(vec3 n) {
	n /= max(abs(n.x) + abs(n.y) + abs(n.z), 1e-8);
	if (n.z < 0.0) n.xy = (1.0 - abs(n.yx)) * sign(n.xy);
	return n.xy;
}

vec3 octDecode(vec2 o) {
	vec3 n = vec3(o.x, o.y, 1.0 - abs(o.x) - abs(o.y));
	if (n.z < 0.0) n.xy = (1.0 - abs(n.yx)) * sign(n.xy);
	return normalize(n);
}

vec3 tangentToWorld(vec3 local, vec3 n) {
	vec3 t;
	vec3 b;
	if (n.z < 0.0) {
		float a = 1.0 / (1.0 - n.z);
		float c = n.x * n.y * a;
		t = vec3(1.0 - n.x * n.x * a, -c, n.x);
		b = vec3(c, n.y * n.y * a - 1.0, -n.y);
	} else {
		float a = 1.0 / (1.0 + n.z);
		float c = -n.x * n.y * a;
		t = vec3(1.0 - n.x * n.x * a, c, -n.x);
		b = vec3(c, 1.0 - n.y * n.y * a, -n.y);
	}
	return normalize(t * local.x + b * local.y + n * local.z);
}

vec3 cosineHemisphere(inout uint rng, vec3 n) {
	float u1 = rand(rng);
	float u2 = rand(rng);
	float r = sqrt(u1);
	float phi = 2.0 * PI * u2;
	return tangentToWorld(vec3(r * cos(phi), r * sin(phi), sqrt(max(1.0 - u1, 0.0))), n);
}

${sdfTraceGlsl()}
`;

function reservoirFrag(): string {
  return /* glsl */ `
in vec2 vUv;
layout(location = 0) out vec4 reservoirOut;
layout(location = 1) out vec4 sampleOut;
layout(location = 2) out vec4 positionOut;
layout(location = 3) out vec4 normalOut;

${COMMON}

uniform sampler2D previousReservoir;
uniform sampler2D previousSample;
uniform sampler2D previousPosition;
uniform sampler2D previousNormal;
uniform sampler2D previousLighting;
uniform sampler2D previousDepth;
uniform mat4 previousViewProjection;
uniform mat4 previousProjInverse;
uniform mat4 previousViewInverse;
uniform vec2 reservoirTexel;
uniform float frame;
uniform float historyValid;

vec3 previousWorldFromDepth(vec2 uv) {
	float depth = texture(previousDepth, uv).r;
	vec4 ndc = vec4(uv * 2.0 - 1.0, depth * 2.0 - 1.0, 1.0);
	vec4 view = previousProjInverse * ndc;
	view /= view.w;
	return (previousViewInverse * view).xyz;
}

bool samplePreviousLighting(vec3 hitPos, out vec3 lo) {
	lo = vec3(0.0);
	if (historyValid < 0.5) return false;
	vec4 clip = previousViewProjection * vec4(hitPos, 1.0);
	if (clip.w < 1e-4) return false;
	vec2 uv = clip.xy / clip.w * 0.5 + 0.5;
	if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) return false;
	vec3 prevP = previousWorldFromDepth(uv);
	float limit = max(sdfVoxelSize * 2.0, 0.08);
	if (distance(prevP, hitPos) > limit) return false;
	lo = texture(previousLighting, uv).rgb;
	return true;
}

void updateReservoir(
	inout vec2 selectedOct, inout float selectedHitT, inout vec3 selectedLo,
	inout float weightSum, inout float selectedTarget, inout float sampleCount,
	vec2 candidateOct, float candidateHitT, vec3 candidateLo,
	float candidateWeight, float candidateTarget, float candidateCount,
	inout uint rng
) {
	if (candidateCount <= 0.0) return;
	sampleCount += candidateCount;
	if (candidateWeight <= 0.0) return;
	weightSum += candidateWeight;
	if (rand(rng) * weightSum < candidateWeight) {
		selectedOct = candidateOct;
		selectedHitT = candidateHitT;
		selectedLo = candidateLo;
		selectedTarget = candidateTarget;
	}
}

bool validHistory(vec2 uv, vec3 p, vec3 n) {
	if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) return false;
	vec4 oldPosition = texture(previousPosition, uv);
	vec3 oldNormal = texture(previousNormal, uv).xyz;
	return oldPosition.w > 0.5 && distance(oldPosition.xyz, p) < 0.45 && dot(oldNormal, n) > 0.88;
}

void reuseReservoir(
	vec2 uv, vec3 p, vec3 n,
	inout vec2 selectedOct, inout float selectedHitT, inout vec3 selectedLo,
	inout float weightSum, inout float selectedTarget, inout float sampleCount,
	inout uint rng
) {
	if (!validHistory(uv, p, n)) return;
	vec4 old = texture(previousReservoir, uv);
	if (old.w <= 0.0) return;
	vec4 oldSample = texture(previousSample, uv);
	vec3 lo = oldSample.rgb;
	vec3 dir;
	float hitT;
	if (old.z < 0.0) {
		dir = octDecode(old.xy);
		hitT = -1.0;
	} else {
		vec3 hitPos = texture(previousPosition, uv).xyz + octDecode(old.xy) * old.z;
		dir = hitPos - p;
		float dist = length(dir);
		if (dist < 1e-4) return;
		dir /= dist;
		hitT = dist;
	}
	float nDotL = dot(n, dir);
	if (nDotL <= 0.0) return;
	float target = lightLuminance(lo) * nDotL;
	float reusedCount = min(old.w, 20.0);
	float reusedWeight = target * oldSample.a * reusedCount;
	updateReservoir(
		selectedOct, selectedHitT, selectedLo, weightSum, selectedTarget, sampleCount,
		octEncode(dir), hitT, lo, reusedWeight, target, reusedCount, rng
	);
}

void main() {
	float depth = sceneDepth(vUv);
	if (depth > 0.9998 || texture(albedoMetalMap, vUv).a < 0.0) {
		reservoirOut = vec4(0.0);
		sampleOut = vec4(0.0);
		positionOut = vec4(0.0);
		normalOut = vec4(0.0);
		return;
	}

	vec3 p = worldFromDepth(vUv);
	if (length(p - cameraPos) >= FADE_END) {
		reservoirOut = vec4(0.0);
		sampleOut = vec4(0.0);
		positionOut = vec4(0.0);
		normalOut = vec4(0.0);
		return;
	}

	Surface surface = surfaceFromGBuffer(vUv, p);
	vec3 V = normalize(cameraPos - p);
	if (dot(surface.normal, V) < 0.0) surface.normal = surface.geoNormal;
	if (dot(surface.normal, V) < 0.0) surface.normal = -surface.normal;
	if (surface.metalness > 0.9) {
		reservoirOut = vec4(0.0);
		sampleOut = vec4(0.0);
		positionOut = vec4(p, 1.0);
		normalOut = vec4(surface.normal, 1.0);
		return;
	}

	uint rng = uint(gl_FragCoord.x) * 1973u + uint(gl_FragCoord.y) * 9277u + uint(frame) * 26699u;
	vec3 origin = p + surface.normal * surfaceBias();
	vec3 dir = cosineHemisphere(rng, surface.normal);
	float nDotL = max(dot(surface.normal, dir), 0.0);
	float hitT = nDotL > 0.0 && sdfShadows > 0.5 ? traceSdfHit(origin, dir, sdfExtent, sdfMaxSteps) : -1.0;
	vec3 lo = vec3(0.0);
	bool includeSample = false;
	if (nDotL <= 0.0) {
		hitT = -1.0;
	} else if (hitT < 0.0) {
		lo = skyIrradianceColor(dir);
		includeSample = true;
	} else if (samplePreviousLighting(origin + dir * hitT, lo)) {
		includeSample = true;
	} else {
		hitT = -1.0;
	}
	lo = clampFirefly(lo);

	vec2 selectedOct = octEncode(dir);
	float selectedHitT = hitT < 0.0 ? -1.0 : hitT;
	vec3 selectedLo = lo;
	float selectedTarget = lightLuminance(lo) * nDotL;
	float weightSum = includeSample && selectedTarget > 0.0 ? lightLuminance(lo) * PI : 0.0;
	float sampleCount = includeSample ? 1.0 : 0.0;

	if (historyValid > 0.5) {
		vec4 previousClip = previousViewProjection * vec4(p, 1.0);
		if (previousClip.w > 1e-4) {
			vec2 previousUv = previousClip.xy / previousClip.w * 0.5 + 0.5;
			reuseReservoir(previousUv, p, surface.normal, selectedOct, selectedHitT, selectedLo,
				weightSum, selectedTarget, sampleCount, rng);
			float angle = rand(rng) * 2.0 * PI;
			for (int i = 0; i < 4; i++) {
				float a = angle + float(i) * 1.57079632679;
				float radius = mix(2.0, 12.0, rand(rng));
				vec2 offset = vec2(cos(a), sin(a)) * radius * reservoirTexel;
				reuseReservoir(previousUv + offset, p, surface.normal, selectedOct, selectedHitT, selectedLo,
					weightSum, selectedTarget, sampleCount, rng);
			}
		}
	}

	if (sampleCount <= 0.0 && nDotL > 0.0) {
		selectedLo = vec3(DISOCCLUSION_LO);
		selectedOct = octEncode(dir);
		selectedHitT = -1.0;
		selectedTarget = lightLuminance(selectedLo) * nDotL;
		weightSum = lightLuminance(selectedLo) * PI;
		sampleCount = 1.0;
	}

	if (sampleCount > MAX_M) {
		weightSum *= MAX_M / sampleCount;
		sampleCount = MAX_M;
	}

	float finalWeight = 0.0;
	if (selectedTarget > 0.0) {
		finalWeight = weightSum / max(sampleCount * selectedTarget, 1e-8);
	}
	reservoirOut = vec4(selectedOct, selectedHitT, sampleCount);
	sampleOut = vec4(selectedLo, min(finalWeight, 2048.0));
	positionOut = vec4(p, 1.0);
	normalOut = vec4(surface.normal, 1.0);
}
`;
}

function shadeFrag(): string {
  return /* glsl */ `
in vec2 vUv;
out vec4 irradianceOut;

${COMMON}

uniform sampler2D reservoirMap;
uniform sampler2D sampleMap;

void main() {
	float depth = sceneDepth(vUv);
	if (depth > 0.9998 || texture(albedoMetalMap, vUv).a < 0.0) {
		irradianceOut = vec4(0.0);
		return;
	}

	vec3 p = worldFromDepth(vUv);
	Surface surface = surfaceFromGBuffer(vUv, p);
	vec3 V = normalize(cameraPos - p);
	if (dot(surface.normal, V) < 0.0) surface.normal = surface.geoNormal;
	if (dot(surface.normal, V) < 0.0) surface.normal = -surface.normal;

	vec4 reservoir = texture(reservoirMap, vUv);
	vec4 sampleValue = texture(sampleMap, vUv);
	float weight = sampleValue.a;
	if (reservoir.w <= 0.0 || weight <= 0.0) {
		irradianceOut = vec4(0.0);
		return;
	}

	vec3 dir = octDecode(reservoir.xy);
	float nDotL = max(dot(surface.normal, dir), 0.0);
	if (nDotL <= 0.0) {
		irradianceOut = vec4(0.0);
		return;
	}
	irradianceOut = vec4(sampleValue.rgb * nDotL * weight * diffuseGain, 1.0);
}
`;
}

const COPY_VERT = /* glsl */ `
out vec2 vUv;
void main() {
	vUv = uv;
	gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

export class RestirGi {
  readonly irradiance: THREE.Texture;
  scale = 0.5;

  private readonly renderer: THREE.WebGLRenderer;
  private readonly passCamera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  private readonly reservoirScene = new THREE.Scene();
  private readonly shadeScene = new THREE.Scene();
  private readonly reservoirMaterial: THREE.ShaderMaterial;
  private readonly shadeMaterial: THREE.ShaderMaterial;
  private readonly reservoirs: THREE.WebGLRenderTarget[];
  private readonly noisy: THREE.WebGLRenderTarget;
  private readonly previousLighting: THREE.WebGLRenderTarget;
  private readonly previousDepth: THREE.WebGLRenderTarget;
  private readonly copyLightingQuad: FullScreenQuad;
  private readonly copyDepthQuad: FullScreenQuad;
  private readonly copyLightingUniforms = { tDiffuse: { value: null as THREE.Texture | null } };
  private readonly copyDepthUniforms = { tDepth: { value: null as THREE.Texture | null } };
  private readonly quadGeometry: THREE.BufferGeometry;
  private readonly previousViewProjection = new THREE.Matrix4();
  private readonly previousProjInverse = new THREE.Matrix4();
  private readonly previousViewInverse = new THREE.Matrix4();
  private readIndex = 0;
  private frame = 0;
  private historyValid = false;
  private readonly dummyLightMap: THREE.DataTexture;
  private _diffuseGain = GI_DIFFUSE_GAIN;
  private _sdfShadowsEnabled = true;
  private _analyticSdfEnabled = true;
  private _analyticBvhEnabled = true;
  private _voxelSpheresEnabled = true;

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    this.quadGeometry = fullscreenTriangle();
    this.dummyLightMap = new THREE.DataTexture(new Float32Array(4), 1, 1, THREE.RGBAFormat, THREE.FloatType);
    this.dummyLightMap.needsUpdate = true;

    const shared = this.makeSharedUniforms();
    this.reservoirMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: {
        ...shared,
        previousReservoir: { value: null },
        previousSample: { value: null },
        previousPosition: { value: null },
        previousNormal: { value: null },
        previousLighting: { value: null },
        previousDepth: { value: null },
        previousViewProjection: { value: new THREE.Matrix4() },
        previousProjInverse: { value: new THREE.Matrix4() },
        previousViewInverse: { value: new THREE.Matrix4() },
        reservoirTexel: { value: new THREE.Vector2(1, 1) },
        frame: { value: 0 },
        historyValid: { value: 0 },
      },
      vertexShader: VERT,
      fragmentShader: reservoirFrag(),
    });
    this.shadeMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: {
        ...this.makeSharedUniforms(),
        reservoirMap: { value: null },
        sampleMap: { value: null },
      },
      vertexShader: VERT,
      fragmentShader: shadeFrag(),
    });

    const reservoirMesh = new THREE.Mesh(this.quadGeometry, this.reservoirMaterial);
    reservoirMesh.frustumCulled = false;
    this.reservoirScene.add(reservoirMesh);
    const shadeMesh = new THREE.Mesh(this.quadGeometry, this.shadeMaterial);
    shadeMesh.frustumCulled = false;
    this.shadeScene.add(shadeMesh);

    this.reservoirs = [this.makeReservoirTarget(), this.makeReservoirTarget()];
    this.noisy = this.makeIrradianceTarget();
    this.irradiance = this.noisy.texture;
    this.previousLighting = this.makeIrradianceTarget();
    this.previousLighting.texture.minFilter = THREE.LinearFilter;
    this.previousLighting.texture.magFilter = THREE.LinearFilter;
    this.previousLighting.texture.wrapS = THREE.ClampToEdgeWrapping;
    this.previousLighting.texture.wrapT = THREE.ClampToEdgeWrapping;
    this.previousDepth = this.makeIrradianceTarget();

    this.copyLightingQuad = new FullScreenQuad(
      new THREE.ShaderMaterial({
        uniforms: this.copyLightingUniforms,
        vertexShader: COPY_VERT,
        fragmentShader: /* glsl */ `
varying vec2 vUv;
uniform sampler2D tDiffuse;
void main() {
	gl_FragColor = texture2D(tDiffuse, vUv);
}
`,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      })
    );
    this.copyDepthQuad = new FullScreenQuad(
      new THREE.ShaderMaterial({
        uniforms: this.copyDepthUniforms,
        vertexShader: COPY_VERT,
        fragmentShader: /* glsl */ `
varying vec2 vUv;
uniform sampler2D tDepth;
void main() {
	gl_FragColor = vec4(texture2D(tDepth, vUv).r, 0.0, 0.0, 1.0);
}
`,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      })
    );
  }

  get diffuseGain(): number {
    return this._diffuseGain;
  }

  set diffuseGain(value: number) {
    const next = clampGiSlider(value, GI_DIFFUSE_GAIN_MIN, GI_DIFFUSE_GAIN_MAX, GI_DIFFUSE_GAIN);
    this._diffuseGain = next;
    this.reservoirMaterial.uniforms.diffuseGain.value = next;
    this.shadeMaterial.uniforms.diffuseGain.value = next;
  }

  get sdfShadowsEnabled(): boolean {
    return this._sdfShadowsEnabled;
  }

  set sdfShadowsEnabled(value: boolean) {
    if (this._sdfShadowsEnabled === value) return;
    this._sdfShadowsEnabled = value;
    const flag = value ? 1 : 0;
    this.reservoirMaterial.uniforms.sdfShadows.value = flag;
    this.shadeMaterial.uniforms.sdfShadows.value = flag;
    this.resetHistory();
  }

  get analyticSdfEnabled(): boolean {
    return this._analyticSdfEnabled;
  }

  set analyticSdfEnabled(value: boolean) {
    if (this._analyticSdfEnabled === value) return;
    this._analyticSdfEnabled = value;
    const flag = value ? 1 : 0;
    this.reservoirMaterial.uniforms.analyticSdf.value = flag;
    this.shadeMaterial.uniforms.analyticSdf.value = flag;
    this.resetHistory();
  }

  get analyticBvhEnabled(): boolean {
    return this._analyticBvhEnabled;
  }

  set analyticBvhEnabled(value: boolean) {
    if (this._analyticBvhEnabled === value) return;
    this._analyticBvhEnabled = value;
    const flag = value ? 1 : 0;
    this.reservoirMaterial.uniforms.analyticBvh.value = flag;
    this.shadeMaterial.uniforms.analyticBvh.value = flag;
    this.resetHistory();
  }

  get voxelSpheresEnabled(): boolean {
    return this._voxelSpheresEnabled;
  }

  set voxelSpheresEnabled(value: boolean) {
    if (this._voxelSpheresEnabled === value) return;
    this._voxelSpheresEnabled = value;
    const flag = value ? 1 : 0;
    this.reservoirMaterial.uniforms.voxelSpheres.value = flag;
    this.shadeMaterial.uniforms.voxelSpheres.value = flag;
    this.resetHistory();
  }

  applyQuality(quality: GraphicsQuality): void {
    this.scale = quality.restirScale;
    this.reservoirMaterial.uniforms.sdfMaxSteps.value = quality.sdfMaxSteps;
    this.shadeMaterial.uniforms.sdfMaxSteps.value = quality.sdfMaxSteps;
    this.resetHistory();
  }

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width * this.scale));
    const h = Math.max(1, Math.floor(height * this.scale));
    for (const target of this.reservoirs) target.setSize(w, h);
    this.noisy.setSize(w, h);
    this.previousLighting.setSize(width, height);
    this.previousDepth.setSize(width, height);
    this.reservoirMaterial.uniforms.reservoirTexel.value.set(1 / w, 1 / h);
    this.resetHistory();
  }

  resetHistory(): void {
    this.historyValid = false;
    this.frame = 0;
  }

  render(
    camera: THREE.PerspectiveCamera,
    gbuffer: GBuffer,
    sdf: VoxelSdfGpu,
    analytic: AnalyticSdfGpu,
    lightMap: THREE.Texture | null = null,
    lightCount = 0,
    timer: PassTimer | null = null
  ): void {
    this.frame++;
    camera.updateMatrixWorld();
    this.bindScene(gbuffer, sdf, analytic, camera, lightMap, lightCount);

    const writeIndex = 1 - this.readIndex;
    const previous = this.reservoirs[this.readIndex];
    const current = this.reservoirs[writeIndex];
    const ru = this.reservoirMaterial.uniforms;
    ru.previousReservoir.value = previous.textures[0];
    ru.previousSample.value = previous.textures[1];
    ru.previousPosition.value = previous.textures[2];
    ru.previousNormal.value = previous.textures[3];
    ru.previousLighting.value = this.previousLighting.texture;
    ru.previousDepth.value = this.previousDepth.texture;
    ru.previousViewProjection.value.copy(this.previousViewProjection);
    ru.previousProjInverse.value.copy(this.previousProjInverse);
    ru.previousViewInverse.value.copy(this.previousViewInverse);
    ru.frame.value = this.frame;
    ru.historyValid.value = this.historyValid ? 1 : 0;

    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = true;
    this.renderer.setRenderTarget(current);
    timePass(timer, 'giReservoir', () => {
      this.renderer.render(this.reservoirScene, this.passCamera);
    });

    this.shadeMaterial.uniforms.reservoirMap.value = current.textures[0];
    this.shadeMaterial.uniforms.sampleMap.value = current.textures[1];
    this.renderer.setRenderTarget(this.noisy);
    timePass(timer, 'giShade', () => {
      this.renderer.render(this.shadeScene, this.passCamera);
    });
    this.renderer.autoClear = previousAutoClear;

    this.readIndex = writeIndex;
  }

  captureLighting(lighting: THREE.Texture, depth: THREE.Texture, camera: THREE.PerspectiveCamera): void {
    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = true;
    this.renderer.setRenderTarget(this.previousLighting);
    this.copyLightingUniforms.tDiffuse.value = lighting;
    this.copyLightingQuad.render(this.renderer);
    this.copyLightingUniforms.tDiffuse.value = null;

    this.renderer.setRenderTarget(this.previousDepth);
    this.copyDepthUniforms.tDepth.value = depth;
    this.copyDepthQuad.render(this.renderer);
    this.copyDepthUniforms.tDepth.value = null;
    this.renderer.autoClear = previousAutoClear;

    camera.updateMatrixWorld();
    this.previousViewProjection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    this.previousProjInverse.copy(camera.projectionMatrixInverse);
    this.previousViewInverse.copy(camera.matrixWorld);
    this.historyValid = true;
  }

  dispose(): void {
    for (const target of this.reservoirs) target.dispose();
    this.noisy.dispose();
    this.previousLighting.dispose();
    this.previousDepth.dispose();
    this.reservoirMaterial.dispose();
    this.shadeMaterial.dispose();
    this.dummyLightMap.dispose();
    this.copyLightingQuad.material.dispose();
    this.copyLightingQuad.dispose();
    this.copyDepthQuad.material.dispose();
    this.copyDepthQuad.dispose();
    this.quadGeometry.dispose();
  }

  private bindScene(
    gbuffer: GBuffer,
    sdf: VoxelSdfGpu,
    analytic: AnalyticSdfGpu,
    camera: THREE.PerspectiveCamera,
    lightMap: THREE.Texture | null,
    lightCount: number
  ): void {
    for (const material of [this.reservoirMaterial, this.shadeMaterial]) {
      const u = material.uniforms;
      u.lightMap.value = lightMap ?? this.dummyLightMap;
      u.lightCount.value = lightMap ? lightCount : 0;
      u.depthMap.value = gbuffer.depth;
      u.albedoMetalMap.value = gbuffer.albedoMetal;
      u.normalRoughMap.value = gbuffer.normalRough;
      u.sdfMap.value = sdf.texture;
      u.sdfOrigin.value.copy(sdf.origin);
      u.sdfExtent.value = sdf.extent;
      u.sdfVoxelSize.value = sdf.extent / sdf.resolution;
      u.planeMap.value = analytic.planes;
      u.triangleMap.value = analytic.triangles;
      u.braceMap.value = analytic.braces;
      u.latticeOriginMap.value = analytic.originMap;
      u.planeCount.value = analytic.planeCount;
      u.triangleCount.value = analytic.triangleCount;
      u.braceCount.value = analytic.braceCount;
      u.projInverse.value.copy(camera.projectionMatrixInverse);
      u.viewInverse.value.copy(camera.matrixWorld);
      u.cameraPos.value.setFromMatrixPosition(camera.matrixWorld);
      u.skyZenith.value.copy(SKY_ZENITH);
      u.skyHorizon.value.copy(SKY_HORIZON);
      u.skyGround.value.copy(SKY_GROUND);
    }
  }

  private makeSharedUniforms(): Record<string, THREE.IUniform> {
    return {
      depthMap: { value: null },
      albedoMetalMap: { value: null },
      normalRoughMap: { value: null },
      lightMap: { value: null },
      lightCount: { value: 0 },
      sdfMap: { value: null },
      planeMap: { value: null },
      triangleMap: { value: null },
      braceMap: { value: null },
      latticeOriginMap: { value: null },
      planeCount: { value: 0 },
      triangleCount: { value: 0 },
      braceCount: { value: 0 },
      sdfMaxSteps: { value: 48 },
      sdfShadows: { value: 1 },
      analyticSdf: { value: 1 },
      analyticBvh: { value: 1 },
      voxelSpheres: { value: 1 },
      diffuseGain: { value: GI_DIFFUSE_GAIN },
      sdfOrigin: { value: new THREE.Vector3() },
      sdfExtent: { value: 24 },
      sdfVoxelSize: { value: 0.25 },
      projInverse: { value: new THREE.Matrix4() },
      viewInverse: { value: new THREE.Matrix4() },
      cameraPos: { value: new THREE.Vector3() },
      skyZenith: { value: SKY_ZENITH.clone() },
      skyHorizon: { value: SKY_HORIZON.clone() },
      skyGround: { value: SKY_GROUND.clone() },
    };
  }

  private makeReservoirTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      count: 4,
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
    });
    for (const texture of target.textures) texture.colorSpace = THREE.NoColorSpace;
    return target;
  }

  private makeIrradianceTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
    });
    target.texture.colorSpace = THREE.NoColorSpace;
    return target;
  }
}
